/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { cloneElement, useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Alert, Button, Box } from "@mui/material";
import { getAccessSession } from "services/userAccess";
import { hasFunction, routeFunction } from "shared/accessPermissions";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";
import { subscribeToAuthChanges } from "services/firebase";
import { clearLegacyTrayCredentials } from "services/tray";

// Images
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { signOutFirebase } from "services/firebase";
import SessionLoading from "components/SessionLoading";
import EmailVerificationGate from "components/EmailVerification/Gate";
import ContractAccessGate from "components/ContractAccess/Gate";
import brandLogo from "assets/images/logos/logo-publique.png";

export default function App() {
  const publicPaths = [
    "/login",
    "/signup",
    "/tray/callback",
    "/tray/callback/auth",
    "/contrato",
    "/verificar-conta",
  ];
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(undefined);
  const [accessError, setAccessError] = useState("");
  const [accessRevision, setAccessRevision] = useState(0);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    clearLegacyTrayCredentials();
    let generation = 0;
    let refreshSequence = 0;
    let active = true;
    let signedInUser = null;
    const load = async (user) => {
      signedInUser = user;
      const current = ++generation;
      setAccessError("");
      setAuthenticatedUser(undefined);
      if (!user) {
        setAuthenticatedUser(null);
        return;
      }
      try {
        const session = await getAccessSession(user);
        if (!session.access)
          throw new Error("Atualize o servidor para habilitar as permissões de usuário.");
        if (active && current === generation)
          setAuthenticatedUser({
            ...user,
            uid: user.uid,
            email: user.email,
            emailVerified: session.emailVerified ?? user.emailVerified,
            displayName: session.access.name || user.displayName,
            access: session.access,
          });
      } catch (error) {
        if (active && current === generation) setAccessError(error.message);
      }
    };
    const refresh = async () => {
      if (!signedInUser) return;
      const current = generation;
      const requestSequence = ++refreshSequence;
      try {
        const session = await getAccessSession(signedInUser);
        if (!session.access) throw new Error("Permissões indisponíveis. Tente novamente.");
        if (active && current === generation && requestSequence === refreshSequence)
          setAuthenticatedUser((previous) =>
            previous &&
            (JSON.stringify(previous.access) !== JSON.stringify(session.access) ||
              previous.emailVerified !== (session.emailVerified ?? signedInUser.emailVerified))
              ? {
                  ...previous,
                  emailVerified: session.emailVerified ?? signedInUser.emailVerified,
                  displayName: session.access.name || signedInUser.displayName,
                  access: session.access,
                }
              : previous
          );
      } catch (error) {
        if (active && current === generation && requestSequence === refreshSequence)
          setAccessError(error.message);
      }
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("pso:access-updated", refresh);
    window.addEventListener("pso:email-verified", refresh);
    window.addEventListener("pso:email-verification-required", refresh);
    const timer = setInterval(refresh, 60000);
    const unsubscribe = subscribeToAuthChanges(load);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pso:access-updated", refresh);
      window.removeEventListener("pso:email-verified", refresh);
      window.removeEventListener("pso:email-verification-required", refresh);
      unsubscribe();
    };
  }, [accessRevision]);

  const isAuthResolved = authenticatedUser !== undefined;
  const isAuthenticated = Boolean(authenticatedUser);
  const isPublicPath = publicPaths.includes(pathname);
  const shouldShowDashboardShell =
    layout === "dashboard" && isAuthenticated && pathname !== "/verificar-conta";

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const isPublicRoute = publicPaths.includes(route.route);
        const shouldKeepPublicRouteAfterSignIn =
          route.route === "/contrato" ||
          route.route === "/verificar-conta" ||
          route.route === "/login" ||
          route.route === "/signup" ||
          route.route.startsWith("/tray/callback");
        const permissionKey = routeFunction(route.route);
        const permitted = !permissionKey || hasFunction(authenticatedUser?.access, permissionKey);
        const routeComponent = route.component
          ? cloneElement(route.component, { user: authenticatedUser })
          : route.component;
        const denial = (
          <DashboardLayout>
            <Box sx={{ py: 4 }}>
              <Alert severity="warning">
                Seu usuário não tem acesso a esta função. Escolha uma opção disponível no menu ou
                fale com o administrador.
              </Alert>
              <Button onClick={() => signOutFirebase()}>Sair da conta</Button>
            </Box>
          </DashboardLayout>
        );
        const destinations = {
          contract: "/contrato",
          admin: "/admin",
          dashboard: "/dashboard",
          "my-publications": "/my-publications",
          "my-orders": "/my-orders",
          tables: "/calculator",
          billing: "/publish-here",
        };
        const firstRoute = authenticatedUser?.access?.functions?.find((key) =>
          hasFunction(authenticatedUser.access, key)
        );
        const denied =
          route.route === "/dashboard" && firstRoute && firstRoute !== "dashboard" ? (
            <Navigate to={destinations[firstRoute]} replace />
          ) : (
            denial
          );
        const element = !isAuthResolved ? null : isAuthenticated && !permitted ? (
          denied
        ) : isPublicRoute ? (
          isAuthenticated && !shouldKeepPublicRouteAfterSignIn ? (
            <Navigate to="/dashboard" replace />
          ) : (
            routeComponent
          )
        ) : isAuthenticated ? (
          permitted ? (
            routeComponent
          ) : (
            denied
          )
        ) : (
          <Navigate to="/login" replace />
        );

        return <Route exact path={route.route} element={element} key={route.key} />;
      }

      return null;
    });

  if (accessError || !isAuthResolved) {
    return (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        <SessionLoading
          error={accessError}
          onRetry={() => setAccessRevision((value) => value + 1)}
          onSignOut={() => signOutFirebase().catch((failure) => setAccessError(failure.message))}
        />
      </ThemeProvider>
    );
  }

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <EmailVerificationGate user={authenticatedUser}>
          <ContractAccessGate user={pathname === "/verificar-conta" ? null : authenticatedUser}>
            {shouldShowDashboardShell && (
              <>
                <Sidenav
                  color={sidenavColor}
                  brand={brandLogo}
                  brandName="Publique sua Obra"
                  user={authenticatedUser}
                  routes={routes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
                <Configurator />
              </>
            )}
            {layout === "vr" && isAuthenticated && <Configurator />}
            <Routes>
              {getRoutes(routes)}
              <Route path="/authentication/sign-in" element={<Navigate to="/login" replace />} />
              <Route
                path="/authentication/sign-in/basic"
                element={<Navigate to="/login" replace />}
              />
              <Route path="/authentication/sign-up" element={<Navigate to="/signup" replace />} />
              <Route
                path="/account/connections"
                element={<Navigate to="/account?tab=profile" replace />}
              />
              <Route
                path="*"
                element={<Navigate to={isPublicPath ? pathname : "/login"} replace />}
              />
            </Routes>
          </ContractAccessGate>
        </EmailVerificationGate>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <EmailVerificationGate user={authenticatedUser}>
        <ContractAccessGate user={pathname === "/verificar-conta" ? null : authenticatedUser}>
          {shouldShowDashboardShell && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={brandLogo}
                brandName="Publique sua Obra"
                user={authenticatedUser}
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
            </>
          )}
          {layout === "vr" && isAuthenticated && <Configurator />}
          <Routes>
            {getRoutes(routes)}
            <Route path="/authentication/sign-in" element={<Navigate to="/login" replace />} />
            <Route
              path="/authentication/sign-in/basic"
              element={<Navigate to="/login" replace />}
            />
            <Route path="/authentication/sign-up" element={<Navigate to="/signup" replace />} />
            <Route
              path="/account/connections"
              element={<Navigate to="/account?tab=profile" replace />}
            />
            <Route
              path="*"
              element={<Navigate to={isPublicPath ? pathname : "/login"} replace />}
            />
          </Routes>
        </ContractAccessGate>
      </EmailVerificationGate>
    </ThemeProvider>
  );
}
